import { Injectable } from '@angular/core';
import { Path } from '@common/data-models/complex-types/path';
import { EnpsField } from '@common/data-models/fields/enps-field';
import { PSTFieldNames, PSTFieldNamesEnum } from '@common/data-models/fields/pst-field-names';
import { RecordFactory } from '@common/data-models/records/record-factory';
import { ViewStateRecord } from '@common/data-models/records/viewstate-record';
import {
    AddressSerializer,
    AttachmentListSerializer,
    ChecklistSerializer,
    ComboboxSerializer,
    CostSerializer,
    CustomFieldsSerializer,
    DateTimeRangeSerializer,
    DurationSerializer,
    LocationListComplexSerializer,
    LocationListSerializer,
    LookupListSerializer,
    PreviewSerializer,
    RelationshipSerializer,
    RichTextSerializer,
    SpecialSerializer,
    StaffIDListSerializer,
    ViewStateDelaySerializer,
    ViewStateFilterSerializer,
    ViewStateOptionsSerializer,
    ViewStateOrderSerializer,
    ViewStateSequenceSerializer,
    YoutubeSerializer,
} from '@common/data-models/serializers/serializers';
import { Layout, LayoutColumn, LayoutType } from '@common/data-models/service/layout';
import { ListData, ListDataMetaData } from '@common/data-models/service/list-data';
import { ObjectProperties } from '@common/data-models/service/object-properties';
import { ServiceCollectionRecord } from '@common/data-models/service/service-collection-record';
import { ServiceCollectionScript } from '@common/data-models/service/service-collection-script';
import { ServiceLayoutColumn } from '@common/data-models/service/service-layout-column';
import { ServiceRecord } from '@common/data-models/service/service-record';
import { DataType } from '@common/enums/data-type';
import { KnownPropertyTypes } from '@common/enums/known-property-types';
import { ObjectTypes } from '@common/enums/object-types';
import { IGenericAccessor } from '@common/interfaces/i-generic-accessor';
import { DataMap } from '@mescius/wijmo.grid';
import { STORY_MOS_PROPERTIES } from '@src/suite/common/enums/constants';
import { FieldDefValidations } from '@src/suite/common/enums/enums';
import { IGlobalTablesService } from '@src/suite/common/interfaces/i-global-tables-service';
import { initializeRecord } from '@src/suite/common/utils/record-utils';
import { secondsToTime } from '@src/suite/common/utils/timing-format';
import { DateTime } from '@suite/common/data-models/complex-types/date-time';
import { Record } from '@suite/common/data-models/records/record';

@Injectable({ providedIn: 'root' })
export class Convertor {
    public globalTablesService: IGlobalTablesService;

    public serializeField(value: any, key: string, viewStateType: number = 0): EnpsField {
        const loweredKey = key.toLowerCase();
        let serializedValue = value;
        let usingRawValue = false;
        if (key === PSTFieldNames.PSTExternalFiles) {
            serializedValue = AttachmentListSerializer.Instance(this.globalTablesService).serialize(value);
        } else if (loweredKey === 'relationships') {
            serializedValue = RelationshipSerializer.Instance(this.globalTablesService).serialize(value);
        } else if (loweredKey === 'text') {
            const text = RichTextSerializer.Instance.serialize(value);
            key = 'Text';
            serializedValue = text;
        } else if (loweredKey === 'textcommands' || loweredKey === 'textcommandkeys' || loweredKey === 'textcommandvalues') {
            serializedValue = value;
        } else if (this.globalTablesService.isDurationFieldType(key)) {
            const numberValue = parseFloat(DurationSerializer.Instance.serialize(value));
            serializedValue = numberValue;
        } else if (this.globalTablesService.isCheckListFieldType(key)) {
            serializedValue = ChecklistSerializer.Instance.serialize(value);
        } else if (loweredKey === 'guid') {
            serializedValue = value;
        } else if (loweredKey === 'path') {
            serializedValue = (<Path>value).fullPath;
        } else if (this.globalTablesService.isComboboxFieldType(key)) {
            serializedValue = ComboboxSerializer.Instance.serialize(value);
        } else if (this.globalTablesService.isLookupListFieldType(key)) {
            serializedValue = LookupListSerializer.Instance(this.globalTablesService).serialize(value);
        } else if (this.globalTablesService.isCustomItemFieldType(key)) {
            serializedValue = CustomFieldsSerializer.Instance(this.globalTablesService).serialize(value);
        } else if (this.globalTablesService.isSpecialFieldType(key)) {
            serializedValue = SpecialSerializer.Instance(this.globalTablesService).serialize(value);
        } else if (this.globalTablesService.isAddressFieldType(key)) {
            serializedValue = AddressSerializer.Instance(this.globalTablesService).serialize(value);
        } else if (this.globalTablesService.FieldDefsDict[key] && this.globalTablesService.isStaffIDListFieldType(key)) {
            serializedValue = StaffIDListSerializer.Instance(this.globalTablesService).serialize(value, key);
        } else if (key === PSTFieldNames.RSLocation) {
            serializedValue = LocationListComplexSerializer.Instance(this.globalTablesService).serialize(value);
        } else if (this.globalTablesService.isLocationListFieldType(key)) {
            serializedValue = LocationListSerializer.Instance(this.globalTablesService).serialize(value);
        } else if (key === PSTFieldNames.PSTViewStateFilter) {
            serializedValue = ViewStateFilterSerializer.Instance(this.globalTablesService).serialize(value);
        } else if (key === PSTFieldNames.PSTTopicOrder) {
            serializedValue = ViewStateOrderSerializer.Instance(this.globalTablesService).serialize(value);
        } else if (this.globalTablesService.isDateRangeFieldType(key)) {
            serializedValue = DateTimeRangeSerializer.Instance().serialize(value);
        } else if (value instanceof DateTime) {
            serializedValue = value.moment?.toISOString() || value.get()?.toISOString();
        } else if (key === PSTFieldNames.PSTViewStateGrouping || key === PSTFieldNames.PSTViewStateSorting || key === PSTFieldNames.PSTViewStateLayout) {
            serializedValue = ViewStateOptionsSerializer.Instance(this.globalTablesService).serialize(value, key, viewStateType);
        } else if (key === PSTFieldNames.PSTViewStateSlideshowSequence) {
            serializedValue = ViewStateSequenceSerializer.Instance.serialize(value);
        } else if (key === PSTFieldNames.PSTViewStateSlideshowDelay) {
            serializedValue = ViewStateDelaySerializer.Instance.serialize(value);
        } else {
            serializedValue = value;
            usingRawValue = true;
        }
        return { key, value: serializedValue, usingRawValue };
    }

    public serialize(rec: StorytellingRecord, sanitize = false): { [id: string]: any } {
        const data: { [id: string]: any } = {};
        let viewStateType = 0;
        if (rec.Type === ObjectTypes.PSTVIEWSTATE) {
            viewStateType = rec?.[PSTFieldNames.PSTViewStateType];
        }

        const { Type } = rec;

        for (const key in rec) {
            // Remove fields that are not in field defs
            if (sanitize && key !== PSTFieldNamesEnum.Title && key !== PSTFieldNamesEnum.Slug && !this.globalTablesService.FieldDefsDict[key]) {
                continue;
            }

            const value = rec[key];
            const serlialized = this.serializeField(value, key, viewStateType);
            data[serlialized.key] = serlialized.value;
        }

        if (sanitize) data.Type = Type;

        return data;
    }

    public serializeOld(rec: Record, sanitize = false): { [id: string]: any } {
        const data: { [id: string]: any } = {};
        let viewStateType = 0;
        if (rec.type === ObjectTypes.PSTVIEWSTATE) {
            viewStateType = rec.getAs<number>(PSTFieldNames.PSTViewStateType);
        }

        const { type } = rec;

        for (const key of rec.keys) {
            // Remove fields that are not in field defs
            if (sanitize && !this.globalTablesService.FieldDefsDict[key]) {
                continue;
            }

            const value = rec.data[key];
            const serlialized = this.serializeField(value, key, viewStateType);
            data[serlialized.key] = serlialized.value;
        }

        if (sanitize) data.Type = type;

        return data;
    }

    public deserialize(rec: Record, data: IGenericAccessor, cachedMetaData?: { [id: string]: number }) {
        const objectType = data.getRawValue('Type');
        let viewStateType = 0;
        if (<number>objectType === ObjectTypes.PSTVIEWSTATE) {
            viewStateType = data.getRawValue(PSTFieldNames.PSTViewStateType);
        }
        const metaData = cachedMetaData || data.getMetaData();

        for (const key in metaData) {
            const fullPath = DataType.fromRaw(data.getRawValue('Path'), DataType.STRING);
            /*if ((fullPath && fullPath.toString().indexOf('\\L\\') > -1) || (fullPath && fullPath.toString().indexOf('\\L') > -1)) {
                // template
                if (key !== 'parentCollectionGuid' && key !== 'Database' && (!this.globalTablesService.FieldDefsDict[key] || !Record.allPropertiesCache[objectType][key]) && !ListDataMetaData[key]) {
                    continue;
                }
            }*/

            if (key === PSTFieldNames.PSTExternalFiles) {
                rec.set(key, AttachmentListSerializer.Instance(this.globalTablesService).deserialize(data, key, objectType));
            } else if (key === 'Relationships') {
                rec.set(key, RelationshipSerializer.Instance(this.globalTablesService).deserialize(data, key));
            } else if (this.globalTablesService.isCheckListFieldType(key)) {
                rec.set(key, ChecklistSerializer.Instance.deserialize(data, key));
            } else if (key.toLowerCase() === 'guid') {
                rec.guid = DataType.fromRaw(data.getRawValue(key), DataType.STRING);
            } else if (key === 'Path') {
                rec.fullPath = fullPath;
            } else if (this.globalTablesService.isComboboxFieldType(key)) {
                rec.set(key, ComboboxSerializer.Instance.deserialize(data, key));
            } else if (this.globalTablesService.isLookupListFieldType(key)) {
                rec.set(key, LookupListSerializer.Instance(this.globalTablesService).deserialize(data, key));
            } else if (this.globalTablesService.isCustomItemFieldType(key)) {
                rec.set(key, CustomFieldsSerializer.Instance(this.globalTablesService).deserialize(data, key));
            } else if (this.globalTablesService.isSpecialFieldType(key)) {
                rec.set(key, SpecialSerializer.Instance(this.globalTablesService).deserialize(data, key, objectType));
            } else if (this.globalTablesService.isAddressFieldType(key)) {
                rec.set(key, AddressSerializer.Instance(this.globalTablesService).deserialize(data, key));
            } else if (this.globalTablesService.isStaffIDListFieldType(key)) {
                rec.set(key, StaffIDListSerializer.Instance(this.globalTablesService).deserialize(data, key));
            } else if (key === PSTFieldNames.RSLocation) {
                rec.set(key, LocationListComplexSerializer.Instance(this.globalTablesService).deserialize(data, key));
            } else if (this.globalTablesService.isLocationListFieldType(key)) {
                rec.set(key, LocationListSerializer.Instance(this.globalTablesService).deserialize(data, key));
            } else if (key === PSTFieldNames.PSTViewStateFilter) {
                rec.set(key, ViewStateFilterSerializer.Instance(this.globalTablesService).deserialize(data, key, viewStateType));
            } else if (key === 'text' || key === 'Text') {
                rec.set('text', RichTextSerializer.Instance.deserialize(data, key));
                const textCommands = data.getRawValue('TextCommands');
                if (textCommands) {
                    rec.set('TextCommands', textCommands);
                }
            } else if (key === PSTFieldNames.PSTViewStateGrouping || key === PSTFieldNames.PSTViewStateSorting || key === PSTFieldNames.PSTViewStateLayout) {
                rec.set(key, ViewStateOptionsSerializer.Instance(this.globalTablesService).deserialize(data, key, viewStateType));
            } else if (key === PSTFieldNames.PSTViewStateSlideshowDelay) {
                rec.set(key, ViewStateDelaySerializer.Instance.deserialize(data, key));
            } else if (key === PSTFieldNames.PSTTopicOrder) {
                rec.set(key, ViewStateOrderSerializer.Instance(this.globalTablesService).deserialize(data, key));
            } else if (this.globalTablesService.isDateRangeFieldType(key)) {
                rec.set(key, DateTimeRangeSerializer.Instance().deserialize(data, key));
            } else if (key === 'TextCommands') {
                if (objectType === ObjectTypes.WIRE) {
                    const preview = PreviewSerializer.Instance.deserialize(data, key);
                    rec.set(PSTFieldNames.PSTWireImage, preview);
                    const youtube = YoutubeSerializer.Instance.deserialize(data, key);
                    rec.set(PSTFieldNames.PSTWireVideo, youtube);
                }
            } else if (key === PSTFieldNames.PSTWirePublished) {
                rec.set(key, DataType.fromRaw(data.getRawValue(key), DataType.PUBLISHEDDATE));
            } else {
                rec.set(key, DataType.fromRaw(data.getRawValue(key), metaData[key]));
            }
        }
    }

    public deserializeV2(rec: StorytellingRecord, data: IGenericAccessor, cachedMetaData?: { [id: string]: number }) {
        if (!rec) {
            rec = {} as StorytellingRecord;
        }
        const objectType = data.getRawValue('Type');
        let viewStateType = 0;
        if (<number>objectType === ObjectTypes.PSTVIEWSTATE) {
            viewStateType = data.getRawValue(PSTFieldNames.PSTViewStateType);
        }
        const metaData = cachedMetaData || data.getMetaData();
        const fullPath = new Path(DataType.fromRaw(data.getRawValue('Path'), DataType.STRING));

        for (const key in metaData) {
            /*if ((fullPath && fullPath.toString().indexOf('\\L\\') > -1) || (fullPath && fullPath.toString().indexOf('\\L') > -1)) {
                // template
                if (key !== 'parentCollectionGuid' && key !== 'Database' && (!this.globalTablesService.FieldDefsDict[key] || !Record.allPropertiesCache[objectType][key]) && !ListDataMetaData[key]) {
                    continue;
                }
            }*/
            if (key === 'BackTime' || key === 'FrontTime' || key === 'CumeTime' || key === 'Cume' || key === 'BackCumeTime' || key === 'Layout') continue;
            if (key === PSTFieldNames.PSTExternalFiles) {
                rec[key] = AttachmentListSerializer.Instance(this.globalTablesService).deserialize(data, key, objectType);
            } else if (key === PSTFieldNames.EditLocked) {
                rec[key] = data.getRawValue(key) === '1';
            } else if (key === PSTFieldNames.PSTRundownSequence) {
                // TODO Fix this somehow later
                const value = data.getRawValue(key);
                if (value?.length !== 1) {
                    rec[key] = value;
                } else {
                    const regex = /[\u0010]/;
                    if (regex.test(value[0])) {
                        rec[key] = value[0].split(regex);
                    } else {
                        rec[key] = value;
                    }
                }
            } else if (key === 'Relationships') {
                rec[key] = RelationshipSerializer.Instance(this.globalTablesService).deserialize(data, key);
            } else if (this.globalTablesService.isCheckListFieldType(key)) {
                rec[key] = ChecklistSerializer.Instance.deserialize(data, key);
            } else if (key.toLowerCase() === 'guid') {
                rec.Guid = DataType.fromRaw(data.getRawValue(key), DataType.STRING);
            } else if (key === 'Path') {
                rec[key] = fullPath;
            } else if (this.globalTablesService.isComboboxFieldType(key)) {
                rec[key] = ComboboxSerializer.Instance.deserialize(data, key);
            } else if (this.globalTablesService.isLookupListFieldType(key)) {
                rec[key] = LookupListSerializer.Instance(this.globalTablesService).deserialize(data, key);
            } else if (this.globalTablesService.isCustomItemFieldType(key)) {
                rec[key] = CustomFieldsSerializer.Instance(this.globalTablesService).deserialize(data, key);
            } else if (this.globalTablesService.isCostFieldType(key)) {
                rec[key] = CostSerializer.Instance(this.globalTablesService).deserialize(data, key, objectType);
            } else if (this.globalTablesService.isSpecialFieldType(key)) {
                rec[key] = SpecialSerializer.Instance(this.globalTablesService).deserialize(data, key, objectType);
            } else if (this.globalTablesService.isAddressFieldType(key)) {
                rec[key] = AddressSerializer.Instance(this.globalTablesService).deserialize(data, key);
            } else if (this.globalTablesService.isStaffIDListFieldType(key)) {
                rec[key] = StaffIDListSerializer.Instance(this.globalTablesService).deserialize(data, key);
            } else if (key === PSTFieldNames.RSLocation) {
                rec[key] = LocationListComplexSerializer.Instance(this.globalTablesService).deserialize(data, key);
            } else if (this.globalTablesService.isLocationListFieldType(key)) {
                rec[key] = LocationListSerializer.Instance(this.globalTablesService).deserialize(data, key);
            } else if (key === PSTFieldNames.PSTViewStateFilter) {
                rec[key] = ViewStateFilterSerializer.Instance(this.globalTablesService).deserialize(data, key, viewStateType);
            } else if (key === 'text' || key === 'Text') {
                rec['text'] = RichTextSerializer.Instance.deserialize(data, key);
                if (rec['text'] == null) {
                    rec['text'] = '';
                }
                rec.Text = rec['text'];
                const textCommands = data.getRawValue('TextCommands');
                if (textCommands) {
                    rec['TextCommands'] = textCommands;
                }
            } else if (key === PSTFieldNames.PSTViewStateGrouping || key === PSTFieldNames.PSTViewStateSorting || key === PSTFieldNames.PSTViewStateLayout) {
                rec[key] = ViewStateOptionsSerializer.Instance(this.globalTablesService).deserialize(data, key, viewStateType);
            } else if (key === PSTFieldNames.PSTViewStateSlideshowDelay) {
                rec[key] = ViewStateDelaySerializer.Instance.deserialize(data, key);
            } else if (STORY_MOS_PROPERTIES[key]) {
                rec[key] = (DataType.fromRaw(data.getRawValue(key), DataType.STRING) as string).trim();
            } else if (key === PSTFieldNames.PSTTopicOrder) {
                rec[key] = ViewStateOrderSerializer.Instance(this.globalTablesService).deserialize(data, key);
            } else if (this.globalTablesService.isDateRangeFieldType(key)) {
                rec[key] = DateTimeRangeSerializer.Instance().deserialize(data, key);
            } else if (key === 'TextCommands') {
                if (objectType === ObjectTypes.WIRE) {
                    const preview = PreviewSerializer.Instance.deserialize(data, key);
                    rec[PSTFieldNames.PSTWireImage] = preview;
                    const youtube = YoutubeSerializer.Instance.deserialize(data, key);
                    rec[PSTFieldNames.PSTWireVideo] = youtube;
                }
            } else if (key === PSTFieldNames.PSTWirePublished && objectType !== ObjectTypes.DISCOVER_WIRE) {
                rec[key] = DataType.fromRaw(data.getRawValue(key), DataType.PUBLISHEDDATE);
            } else if (key === PSTFieldNames.PSTWirePublished && objectType === ObjectTypes.DISCOVER_WIRE) {
                rec[key] = DataType.fromRaw(data.getRawValue(key), DataType.DISCOVERDATE);
            } else if (key === PSTFieldNames.BackTimeFormatted) {
                rec.BackTime = DataType.fromRaw(data.getRawValue(key), DataType.STRING);
            } else if (key === PSTFieldNames.FrontTimeFormatted) {
                rec.FrontTime = DataType.fromRaw(data.getRawValue(key), DataType.STRING);
            } else if (key === PSTFieldNames.CumeTimeFormatted) {
                rec.CumeTime = DataType.fromRaw(data.getRawValue(key), DataType.STRING);
            } else if (key === PSTFieldNames.BackCumeTimeFormatted) {
                rec.BackCumeTime = DataType.fromRaw(data.getRawValue(key), DataType.STRING);
            } else if (key === PSTFieldNames.ReadTime) {
                rec.ReadTime = DataType.fromRaw(data.getRawValue(key), DataType.NUMBER);
            } else if (key === PSTFieldNames.Actual) {
                const actualValue = data.getRawValue(key);
                if (actualValue) {
                    rec.Actual = DataType.fromRaw(actualValue, DataType.STRING);
                    rec.isActualEnteredManually = true;
                } else {
                    rec.Actual = secondsToTime(DataType.fromRaw(data.getRawValue(PSTFieldNames.ReadTime), DataType.STRING));
                    rec.isActualEnteredManually = false;
                }
            } else if (key === PSTFieldNames.Title) {
                if (objectType === ObjectTypes.SCRIPT) {
                    const titleChunks: Array<string> = DataType.fromRaw(data.getRawValue(key), DataType.STRING)?.split('-');
                    rec.Slug = titleChunks[0] || '';
                    rec.Title = DataType.fromRaw(data.getRawValue(key), DataType.STRING);
                    titleChunks.shift();
                    rec.Segment = titleChunks.join('-');
                } else {
                    rec[key] = DataType.fromRaw(data.getRawValue(key), metaData[key]);
                }
            } else if (this.globalTablesService.isDateTimeFieldType(key)) {
                const newDateTime = DataType.fromRaw(data.getRawValue(key), metaData[key]);
                if (rec[key]) {
                    newDateTime.timeZone = rec[key].timeZone;
                }
                rec[key] = newDateTime;
            } else {
                rec[key] = DataType.fromRaw(data.getRawValue(key), metaData[key]);
            }
        }
    }

    public fromServiceRecordImplV2<T extends StorytellingRecord>(data: IGenericAccessor, cachedMetaData?: { [id: string]: number }, createMethod?: (number) => T): T {
        const ret = <T>createMethod(data.getRawValue('Type'));
        if (!ret) return;
        this.deserializeV2(ret, data, cachedMetaData);
        return ret;
    }

    public fromServiceRecordV2<T extends StorytellingRecord>(data: IGenericAccessor, cachedMetaData?: { [id: string]: number }): T {
        return this.fromServiceRecordImplV2<T>(data, cachedMetaData, initializeRecord);
    }

    public fromServiceDefaultLayout(defaultLayout: { DefaultLayout: Array<ServiceLayoutColumn>; DefaultLayoutType: number }): Layout {
        const ret = new Layout();
        ret.type = defaultLayout.DefaultLayoutType;
        for (const serviceCol of defaultLayout.DefaultLayout) {
            const col = new LayoutColumn();
            col.autoSizeWidth = serviceCol.ColumnAutoSizeWidth;
            if (serviceCol.ColumnName === 'RowStatus' || serviceCol.ColumnName === 'SegStatus') {
                col.displayName = '';
            } else {
                col.displayName = serviceCol.ColumnDisplayName;
            }
            col.extractTarget = serviceCol.ColumnExtractTarget;
            col.justification = serviceCol.ColumnJustification;
            col.list = serviceCol.ColumnList;
            col.maxLen = serviceCol.ColumnMaxLen;
            col.name = serviceCol.ColumnName;
            col.validation = serviceCol.ColumnValidation.toUpperCase();
            if (col.name === PSTFieldNames.ModBy) {
                col.validation = FieldDefValidations.STAFFIDLIST;
            }
            col.width = serviceCol.ColumnWidth / 15;
            col.wordWrap = serviceCol.ColumnWordWrap;
            col.isReadOnly = serviceCol.IsReadOnly;
            col.sortOrder = serviceCol.SortOrder;
            if (col.validation === FieldDefValidations.STAFFIDLIST) {
                col.columnList = Object.values(this.globalTablesService.Staff).map(staff => ({ id: staff.UserID, title: staff.DisplayName }));
                col.columnListDataMap = new DataMap(Object.values(this.globalTablesService.Staff), 'UserID', 'DisplayName');
            } else {
                col.columnList = (serviceCol.ColumnList?.split('|') || []).map(item => ({ id: item, title: item }));
                col.columnListDataMap = new DataMap(serviceCol.ColumnList?.split('|') || [], 'id', 'title');
            }
            if (col.name === PSTFieldNamesEnum.Slug) {
                col.allowMerging = true;
            }

            ret.columns.push(col);
        }
        // Sort
        ret.columns = ret.columns.sort((a, b) => a.sortOrder - b.sortOrder);
        return ret;
    }

    public fromServiceLayout(layoutColumns: Array<ServiceLayoutColumn>, layoutType: LayoutType): Layout {
        const ret = new Layout();
        ret.type = layoutType;
        for (const serviceCol of layoutColumns) {
            const col = new LayoutColumn();
            col.autoSizeWidth = serviceCol.ColumnAutoSizeWidth;
            if (serviceCol.ColumnName === 'RowStatus' || serviceCol.ColumnName === 'SegStatus') {
                col.displayName = '';
            } else {
                col.displayName = serviceCol.ColumnDisplayName;
            }
            col.extractTarget = serviceCol.ColumnExtractTarget;
            col.justification = serviceCol.ColumnJustification;
            col.list = serviceCol.ColumnList;
            col.maxLen = serviceCol.ColumnMaxLen;
            col.name = serviceCol.ColumnName;
            if (col.name === PSTFieldNames.ModBy) {
                col.validation = FieldDefValidations.STAFFIDLIST;
            }
            col.validation = serviceCol.ColumnValidation.toUpperCase();
            col.width = serviceCol.ColumnWidth / 15;
            col.wordWrap = serviceCol.ColumnWordWrap;
            col.isReadOnly = serviceCol.IsReadOnly;
            col.sortOrder = serviceCol.SortOrder;
            if (col.validation === FieldDefValidations.STAFFIDLIST) {
                col.columnList = Object.values(this.globalTablesService.Staff).map(staff => ({ id: staff.UserID, title: staff.DisplayName }));
                col.columnListDataMap = new DataMap(Object.values(this.globalTablesService.Staff), 'UserID', 'DisplayName');
            } else {
                if (col.name === PSTFieldNamesEnum.ObjectAutoCreate) {
                    col.columnList = this.globalTablesService.MOSDevices.filter(item => item.AutoCreate).map(item => ({ id: item.AliasValue || item.ID, title: item.AliasValue || item.ID }));
                    col.columnList.unshift({ id: '', title: '' });
                } else {
                    col.columnList = (serviceCol.ColumnList?.split('|') || []).map(item => ({ id: item, title: item }));
                    col.columnListDataMap = new DataMap(serviceCol.ColumnList?.split('|') || [], 'id', 'title');
                }
            }
            if (serviceCol.ColumnName === PSTFieldNamesEnum.Slug) {
                col.allowMerging = true;
            }
            ret.columns.push(col);
        }
        // Sort
        ret.columns = ret.columns.sort((a, b) => a.sortOrder - b.sortOrder);
        return ret;
    }

    private fromServiceCollectionRecordImplV2<T extends StorytellingCollectionRecord>(data: ServiceCollectionRecord, createMethod: (number) => T): T {
        const ret = <T>createMethod(data.getRawValue('Type'));
        const layoutTypeMaps = {};
        if (data.DefaultLayout) {
            for (const col of data.DefaultLayout.DefaultLayout) {
                layoutTypeMaps[col.ColumnName] = KnownPropertyTypes.forValidationType(col.ColumnValidation);
            }
        }
        KnownPropertyTypes.setFor(layoutTypeMaps);
        this.deserializeV2(ret, data);
        let cachedMetaData: { [id: string]: number } = null;
        const children: Array<StorytellingRecord> = [];
        ret.timingObj = data.TimingObj;
        if (data.CollectionScripts) {
            for (let i = 0; i < data.CollectionScripts.length; i++) {
                const serviceCollectionScriptData = data.CollectionScripts[i];
                const serviceCollectionScript = Object.assign(new ServiceCollectionScript(), serviceCollectionScriptData);
                serviceCollectionScript.instantiateChildren();
                cachedMetaData = serviceCollectionScript.getMetaData();
                const child = this.fromServiceRecordV2(serviceCollectionScript, cachedMetaData);
                children.push(child);
            }
            ret.children = children;
        }
        const stories: Array<StorytellingRecord> = [];
        if (data.Stories?.length > 0) {
            for (let i = 0; i < data.Stories.length; i++) {
                const serviceCollectionScriptData = data.Stories[i];
                const serviceCollectionScript = Object.assign(new ServiceRecord(), serviceCollectionScriptData);
                serviceCollectionScript.instantiateChildren();
                cachedMetaData = serviceCollectionScript.getMetaData();
                const child = this.fromServiceRecordV2(serviceCollectionScript, cachedMetaData);
                stories.push(child);
            }
            ret.stories = stories;
        }
        ret.children = children;
        return ret;
    }

    public fromServiceCollectionRecordV2<T extends StorytellingCollectionRecord>(data: ServiceCollectionRecord): T {
        let ret: T = null;
        ret = this.fromServiceCollectionRecordImplV2<T>(data, initializeRecord);
        if (data.DefaultLayout) ret.Layout = this.fromServiceDefaultLayout(data.DefaultLayout);
        return ret;
    }

    public fromServiceViewStateRecord<T extends ViewStateRecord>(data: ServiceRecord): T {
        data.instantiateChildren();
        const viewStateType = data.getRawValue('PSTViewStateType');
        const rec = RecordFactory.createViewStateForType<T>(viewStateType);
        this.deserialize(rec, data);
        return rec;
    }

    public toServiceViewStateRecord(rec: ViewStateRecord): ServiceRecord {
        const serviceRecord: ServiceRecord = new ServiceRecord();
        serviceRecord.ListData = new ListData();

        const data = this.serializeOld(rec);
        const properties: { [id: string]: any } = {};

        for (const key in data) {
            if (ListDataMetaData[key]) {
                serviceRecord.ListData[key] = data[key];
            } else {
                properties[key] = data[key];
            }
        }
        serviceRecord.ObjectProperties = this.toENPSFields(properties);
        serviceRecord.ObjectPropertiesCollection = new ObjectProperties(serviceRecord.ObjectProperties);

        return serviceRecord;
    }

    public fromServiceExportFormatRecord(data: ServiceRecord): StorytellingExportFormatRecord {
        data.instantiateChildren();
        return <StorytellingExportFormatRecord>this.fromServiceRecordV2(data);
    }

    public fromServiceUserRecord(data: ServiceCollectionRecord): StorytellingUserRecord {
        data.instantiateChildren();
        return <StorytellingUserRecord>this.fromServiceRecordV2(data);
    }

    public toENPSFields(obj: { [id: string]: any }): Array<{ FieldName: string; FieldValue: any }> {
        const ret = new Array<{ FieldName: string; FieldValue: any }>();
        for (const prop in obj) {
            if (prop === 'ExternalFiles') {
                if (obj[prop]?.items?.length === 0) {
                    ret.push({ FieldName: prop, FieldValue: '' });
                } else {
                    ret.push({ FieldName: prop, FieldValue: AttachmentListSerializer.Instance(this.globalTablesService).serialize(obj[prop]) });
                }
            } else {
                ret.push({ FieldName: prop, FieldValue: typeof obj[prop] === 'undefined' ? '' : obj[prop] });
            }
        }
        return ret;
    }
}
